import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billsAPI } from '../services/api';
import { Bill } from '../types';
import { AppLayout } from '../components/layout/AppLayout';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';

const Activity: React.FC = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'settled'>('all');

  useEffect(() => {
    loadBills();
  }, []);

  const loadBills = async () => {
    try {
      const data = await billsAPI.getAll();
      setBills(data.bills);
    } catch (error) {
      console.error('Failed to load bills:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBills = bills.filter((bill) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return bill.status !== 'settled';
    if (filter === 'settled') return bill.status === 'settled';
    return true;
  });

  // Group bills by date
  const groupedBills = filteredBills.reduce((groups, bill) => {
    const date = new Date(bill.created_at).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(bill);
    return groups;
  }, {} as Record<string, Bill[]>);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      <Header title="Activity" />

      <main className="p-4 pb-24 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {(['all', 'pending', 'settled'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
            >
              {f === 'all' ? 'All Bills' : f === 'pending' ? 'Pending' : 'Settled'}
            </button>
          ))}
        </div>

        {filteredBills.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {filter === 'all' ? 'No bills yet' : `No ${filter} bills`}
              </h3>
              <p className="text-slate-500 max-w-xs mx-auto">
                {filter === 'all'
                  ? 'Scan your first bill to start tracking expenses'
                  : `You don't have any ${filter} bills`}
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedBills).map(([date, dateBills]) => (
              <div key={date}>
                <h3 className="text-sm font-medium text-slate-500 mb-2 px-1">{date}</h3>
                <div className="space-y-2">
                  {dateBills.map((bill) => (
                    <Card
                      key={bill.id}
                      hover
                      onClick={() => navigate(`/bills/${bill.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {bill.restaurant_name || 'Unknown Restaurant'}
                          </p>
                          <p className="text-sm text-slate-500 truncate">
                            {bill.group_name || 'Unknown Group'}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-slate-900">
                            ₹{Number(bill.total).toLocaleString('en-IN')}
                          </p>
                          <Badge
                            variant={
                              bill.status === 'settled' ? 'success' :
                              bill.status === 'finalized' ? 'default' : 'warning'
                            }
                            size="sm"
                          >
                            {bill.status}
                          </Badge>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </AppLayout>
  );
};

export default Activity;
